import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle, AlertCircle, X } from 'lucide-react';

const ToastContext = createContext(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({
  children,
}) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(
    (messageOrOptions, type) => {
      const id = Date.now() + Math.random();

      const toastData = typeof messageOrOptions === 'string'
        ? { message: messageOrOptions, type: type || 'success' }
        : messageOrOptions;

      let newToasts = [...toasts, { id, ...toastData }];

      const duration = typeof messageOrOptions === 'object' && messageOrOptions.duration
        ? messageOrOptions.duration
        : toastData.type === 'warning'
          ? 6000
          : toastData.type === 'error'
            ? 'permanent'
            : 1500;

      if (duration !== 'permanent') {
        setTimeout(() => {
          setToasts(prev => prev.filter(toast => toast.id !== id));
        }, duration);
      }

      if (toasts.length >= 3) {
        newToasts = newToasts.slice(1);
      }

      setToasts(newToasts);
    },
    [toasts]
  );

  const getToastClasses = (type) => {
    const baseClasses = 'flex items-center p-4 rounded-lg shadow-md pointer-events-auto mb-2 border';
    const typeClasses = {
      success: 'bg-emerald-50 border-emerald-200',
      error: 'bg-red-50 border-red-200',
      warning: 'bg-amber-50 border-amber-200',
    };
    return `${baseClasses} ${typeClasses[type]}`;
  };

  const getIconClasses = (type) => {
    const colorClasses = {
      success: 'text-emerald-600',
      error: 'text-red-600',
      warning: 'text-amber-600',
    };
    return `w-6 h-6 mr-3 mt-0.5 flex-shrink-0 ${colorClasses[type]}`;
  };

  const getTextClasses = (type) => {
    const colorClasses = {
      success: 'text-emerald-900',
      error: 'text-red-900',
      warning: 'text-amber-900',
    };
    return colorClasses[type];
  };

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-[106px] right-4 z-[9999] flex flex-col gap-2 w-96 pointer-events-none">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={getToastClasses(toast.type)}
            role="alert"
            aria-live="polite"
          >
            {toast.type === 'success' ? (
              <CheckCircle className={getIconClasses('success')} />
            ) : toast.type === 'warning' ? (
              <AlertCircle className={getIconClasses('warning')} />
            ) : (
              <AlertTriangle className={getIconClasses('error')} />
            )}
            <div className="flex-1 flex flex-col gap-1">
              {toast.title && (
                <span className={`text-sm font-semibold ${getTextClasses(toast.type)}`}>{toast.title}</span>
              )}
              <span className={`text-sm font-medium ${getTextClasses(toast.type)}`}>{toast.message}</span>
              {toast.details && (
                <span className={`text-xs font-normal opacity-80 ${getTextClasses(toast.type)}`}>
                  {toast.details}
                </span>
              )}
              {toast.items && (
                <ul className={`list-disc pl-[15px] m-0 font-normal text-xs ${getTextClasses(toast.type)}`}>
                  {toast.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              className="ml-4 p-1 rounded bg-transparent border-none cursor-pointer flex items-center justify-center"
              aria-label="Close notification"
            >
              <X size={16} className={getIconClasses(toast.type)} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
